import { Controller } from 'stimulus'
import queryString from 'query-string'

let debounce = require('lodash/debounce')

const MIN_QUERY_LENGTH = 3
const ACTIVE_MEMBER_LABEL = 'Active member'
const MEMBERSHIP_NOT_FOUND_LABEL = 'Membership not found'

export default class extends Controller {
  static targets = ['query', 'results', 'sfid', 'status']

  initialize() {
    this.search = debounce(this.search, 300)
    this.selectedName = null
  }

  connect() {
    if (this.sfidTarget.value && this.queryTarget.value) {
      this.selectedName = this.queryTarget.value
      this.showStatus(ACTIVE_MEMBER_LABEL)
    }
  }

  onInput() {
    if (
      this.selectedName !== null &&
      this.queryTarget.value !== this.selectedName
    ) {
      this.clearSelection({ clearStatus: true })
    }

    // Keep the list closed when the current value is an intentional selection.
    if (this.hasLockedSelection()) {
      this.clearResults()
      return
    }

    this.search()
  }

  search() {
    if (this.hasLockedSelection()) {
      this.clearResults()
      return
    }

    const query = this.queryTarget.value.trim()

    if (query.length < MIN_QUERY_LENGTH) {
      this.clearResults()
      return
    }

    const url = this.data.get('url')
    fetch(`${url}?${queryString.stringify({ q: query })}`, { method: 'GET' })
      .then((response) => response.json())
      .then((data) => {
        if (this.hasLockedSelection()) {
          this.clearResults()
          return
        }

        this.renderResults(data.results || [])
      })
      .catch(() => this.clearResults())
  }

  blur() {
    window.setTimeout(() => {
      this.clearResults()

      const query = this.queryTarget.value.trim()

      if (query === '') {
        this.hideStatus()
      } else if (this.sfidTarget.value) {
        this.showStatus(ACTIVE_MEMBER_LABEL)
      } else {
        this.showStatus(MEMBERSHIP_NOT_FOUND_LABEL)
      }
    }, 150)
  }

  select(event) {
    event.preventDefault()

    const button = event.currentTarget
    const name = button.dataset.name
    const sfid = button.dataset.sfid

    this.queryTarget.value = name
    this.sfidTarget.value = sfid
    this.selectedName = name

    if (typeof this.search.cancel === 'function') {
      this.search.cancel()
    }

    this.clearResults()
    this.showStatus(ACTIVE_MEMBER_LABEL)

    // Refresh the character counter without reopening the typeahead.
    this.queryTarget.dispatchEvent(new Event('input', { bubbles: true }))
    this.queryTarget.blur()
  }

  renderResults(results) {
    this.resultsTarget.innerHTML = ''

    if (!results.length) {
      this.resultsTarget.hidden = true
      return
    }

    const list = document.createElement('ul')
    list.className = 'complaint-org-search__menu search-suggest__menu'
    list.setAttribute('role', 'listbox')

    results.forEach((result) => {
      const item = document.createElement('li')
      item.className = 'complaint-org-search__item'

      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'complaint-org-search__option'
      button.textContent = result.name
      button.dataset.name = result.name
      button.dataset.sfid = result.sfid
      button.setAttribute('role', 'option')
      button.addEventListener('mousedown', (event) => {
        event.preventDefault()
        this.select(event)
      })

      item.appendChild(button)
      list.appendChild(item)
    })

    this.resultsTarget.appendChild(list)
    this.resultsTarget.hidden = false
  }

  clearResults() {
    this.resultsTarget.innerHTML = ''
    this.resultsTarget.hidden = true
  }

  clearSelection({ clearStatus = false } = {}) {
    this.sfidTarget.value = ''
    this.selectedName = null

    if (clearStatus) {
      this.hideStatus()
    }
  }

  hasLockedSelection() {
    return (
      this.sfidTarget.value !== '' &&
      this.selectedName !== null &&
      this.queryTarget.value === this.selectedName
    )
  }

  showStatus(text) {
    this.statusTarget.textContent = text
    this.statusTarget.hidden = false
  }

  hideStatus() {
    this.statusTarget.textContent = ''
    this.statusTarget.hidden = true
  }
}
